import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { useTheme } from '@/lib/ThemeContext';
import { toast } from 'sonner';
import { Send, Hash, MessageSquare, Plus, X, Search, SmilePlus, CheckCheck, Settings as SettingsIcon, Upload, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { avatarColorClasses } from '@/lib/avatarColor';
import {
  ensureGeneralChannel, listAllChannels, createChannel,
  listStaffDirectory, listChannelMessages, listDMMessages, listRecentDMCounterparts,
  sendChannelMessage, sendDirectMessage, subscribeStaffMessages, subscribeChannels,
  subscribePresence, listReactions, toggleReaction, subscribeReactions,
  channelConversationKey, dmConversationKey, markConversationRead, listConversationReads, subscribeReads,
  uploadChatBackground,
} from '@/lib/teamChat';

// Chat background is a personal display preference, not shared team data --
// stored per-user in this browser's localStorage rather than the database,
// same lightweight pattern already used for the Invoices tab's per-user
// "seen" tracking. Value is always a valid CSS background-image string
// (either a gradient, for the built-in presets, or url(...) for an upload)
// so applying it is the same one-liner either way.
const chatBgKey = (userId) => `prohaul.chat_bg_${userId}`;
const getChatBackground = (userId) => {
  try { return localStorage.getItem(chatBgKey(userId)) || null; } catch { return null; }
};
const setChatBackgroundStorage = (userId, value) => {
  try {
    if (value) localStorage.setItem(chatBgKey(userId), value);
    else localStorage.removeItem(chatBgKey(userId));
  } catch { /* localStorage unavailable -- setting just won't persist */ }
};

const BG_PRESETS = [
  { label: 'Midnight', value: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' },
  { label: 'Ember', value: 'linear-gradient(135deg, #2d1b1b 0%, #3d1f1f 50%, #1a1a2e 100%)' },
  { label: 'Forest', value: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)' },
  { label: 'Slate', value: 'linear-gradient(135deg, #232526 0%, #414345 100%)' },
  { label: 'Dusk', value: 'linear-gradient(135deg, #360033 0%, #0b8793 100%)' },
];

// staff_role values (see AuthContext/staff_profiles) are
// 'super_admin' for the carrier owner's mirrored row -- not 'admin' --
// so that key must match exactly or the role silently falls back to
// the raw, un-cased DB value.
const ROLE_LABELS = { owner: 'Owner', super_admin: 'Super Admin', hr: 'HR', accounting: 'Accounting', dispatch: 'Dispatch' };
const REACTION_EMOJIS = ['👍', '❤️', '😂', '🎉', '👀', '✅'];
const GROUP_WINDOW_MS = 5 * 60 * 1000;

function Avatar({ userId, name, avatarUrl, size = 'w-8 h-8', online }) {
  return (
    <div className={cn('relative flex-shrink-0', size)}>
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className={cn(size, 'rounded-full object-cover')} />
      ) : (
        <div className={cn(size, 'rounded-full flex items-center justify-center text-xs font-semibold', avatarColorClasses(userId))}>
          {(name || '?').trim().slice(0, 1).toUpperCase()}
        </div>
      )}
      {online && <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-background" />}
    </div>
  );
}

function formatDateLabel(date) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((today - target) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString([], {
    weekday: diffDays < 7 ? 'long' : undefined,
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

function DateSeparator({ label }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="flex-1 h-px bg-border" />
      <span className="text-xs text-muted-foreground font-medium px-1">{label}</span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

export default function TeamChat() {
  const { user, carrierId, carrierPlan } = useAuth();
  const { isDark } = useTheme();
  const [channels, setChannels] = useState([]);
  const [dmConversations, setDmConversations] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [selected, setSelected] = useState(null); // { type: 'channel'|'dm', id, label }
  const [messages, setMessages] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [reads, setReads] = useState([]);
  const [onlineIds, setOnlineIds] = useState(new Set());
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pickerFor, setPickerFor] = useState(null);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showChatSettings, setShowChatSettings] = useState(false);
  // Read synchronously on first render (not via a post-mount effect) so
  // there's no flash-of-no-background frame when this page remounts --
  // React Router fully unmounts/remounts route components on navigation,
  // so a `useEffect` here would apply the saved background one paint
  // late every time you navigate away and back.
  const [chatBackground, setChatBackground] = useState(() => (user?.id ? getChatBackground(user.id) : null));
  const bottomRef = useRef(null);

  // Only needed for the case where `user.id` isn't known yet at first
  // render (auth still resolving) or changes to a different account
  // without a full page reload.
  useEffect(() => {
    if (user?.id) setChatBackground(getChatBackground(user.id));
  }, [user?.id]);

  const applyChatBackground = (value) => {
    setChatBackground(value);
    if (user?.id) setChatBackgroundStorage(user.id, value);
  };

  const directoryMap = useMemo(() => new Map(directory.map(d => [d.user_id, d])), [directory]);

  const nameFor = (userId) => {
    if (userId === user?.id) return 'You';
    return directoryMap.get(userId)?.full_name || directoryMap.get(userId)?.user_email || 'Unknown';
  };

  const roleFor = (userId) => {
    const role = directoryMap.get(userId)?.staff_role;
    return role ? (ROLE_LABELS[role] || role) : null;
  };

  const avatarUrlFor = (userId) => directoryMap.get(userId)?.avatar_url || null;

  const loadSidebar = useCallback(async () => {
    if (!carrierId || !user?.id) return;
    try {
      // ensureGeneralChannel used to be awaited before the Promise.all below,
      // adding a full extra sequential round-trip to every load even though
      // it's a cheap SELECT-then-maybe-INSERT that almost always just reads
      // an existing row -- that serial wait was on the critical path for
      // messages to start loading (loadThread depends on `selected`, which
      // depends on `channels` here). Run it in parallel instead; on a
      // carrier's very first-ever Team Chat open, its INSERT may not be
      // visible to the listAllChannels SELECT that raced it, so merge it
      // in below rather than relying on a second round-trip to pick it up.
      const [general, allChannels, dms, dir] = await Promise.all([
        ensureGeneralChannel(carrierId, user.id).catch(() => null),
        listAllChannels(carrierId),
        listRecentDMCounterparts(carrierId, user.id),
        listStaffDirectory(),
      ]);
      const channelsWithGeneral = general && !allChannels.some(c => c.id === general.id)
        ? [general, ...allChannels]
        : allChannels;
      setChannels(channelsWithGeneral);
      setDmConversations(dms);
      setDirectory(dir);
    } catch (e) {
      // A failure here left the page stuck on its loading spinner
      // forever with no feedback at all -- surface it instead.
      toast.error(e?.message || 'Could not load Team Chat.');
    } finally {
      setLoading(false);
    }
  }, [carrierId, user?.id]);

  useEffect(() => { loadSidebar(); }, [loadSidebar]);

  // Default to #general once channels load, if nothing selected yet.
  useEffect(() => {
    if (!selected && channels.length > 0) {
      setSelected({ type: 'channel', id: channels[0].id, label: `#${channels[0].name}` });
    }
  }, [channels, selected]);

  // Who's currently online, carrier-wide -- ephemeral presence, no table.
  useEffect(() => {
    if (!carrierId || !user?.id) return;
    return subscribePresence(carrierId, user.id, setOnlineIds);
  }, [carrierId, user?.id]);

  const loadThread = useCallback(async () => {
    if (!selected) { setMessages([]); return; }
    try {
      const data = selected.type === 'channel'
        ? await listChannelMessages(selected.id)
        : await listDMMessages(carrierId, user.id, selected.id);
      setMessages(data);
    } catch (e) {
      toast.error(e?.message || 'Could not load messages.');
    }
  }, [selected, carrierId, user?.id]);

  useEffect(() => { loadThread(); setPickerFor(null); }, [loadThread]);

  const loadReactions = useCallback(async () => {
    if (!messages.length) { setReactions([]); return; }
    try {
      setReactions(await listReactions(messages.map(m => m.id)));
    } catch { /* non-critical */ }
  }, [messages]);

  useEffect(() => { loadReactions(); }, [loadReactions]);

  const conversationKey = useMemo(() => {
    if (!selected || !user?.id) return null;
    return selected.type === 'channel' ? channelConversationKey(selected.id) : dmConversationKey(user.id, selected.id);
  }, [selected, user?.id]);

  const loadReads = useCallback(async () => {
    if (!conversationKey) { setReads([]); return; }
    try { setReads(await listConversationReads(conversationKey)); } catch { /* non-critical */ }
  }, [conversationKey]);

  useEffect(() => { loadReads(); }, [loadReads]);

  // Mark the open thread read whenever it changes or new messages land.
  useEffect(() => {
    if (!conversationKey || !carrierId || !user?.id) return;
    markConversationRead(carrierId, user.id, conversationKey).then(loadReads).catch(() => {});
  }, [conversationKey, carrierId, user?.id, messages.length]);

  useEffect(() => {
    if (!carrierId) return;
    const unsubMsgs = subscribeStaffMessages(carrierId, () => { loadThread(); loadSidebar(); });
    const unsubChannels = subscribeChannels(carrierId, () => loadSidebar());
    const unsubReactions = subscribeReactions(carrierId, () => loadReactions());
    const unsubReads = subscribeReads(carrierId, () => loadReads());
    return () => { unsubMsgs(); unsubChannels(); unsubReactions(); unsubReads(); };
  }, [carrierId, loadThread, loadSidebar, loadReactions, loadReads]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !selected) return;
    setSending(true);
    setInput('');
    try {
      if (selected.type === 'channel') {
        await sendChannelMessage(carrierId, selected.id, user.id, text);
      } else {
        await sendDirectMessage(carrierId, user.id, selected.id, text);
      }
      await loadThread();
    } catch (e) {
      toast.error(e?.message || 'Could not send message.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleStartDM = (staff) => {
    setShowNewMessage(false);
    setSelected({ type: 'dm', id: staff.user_id, label: staff.full_name || staff.user_email });
  };

  const handleToggleReaction = async (messageId, emoji) => {
    setPickerFor(null);
    try {
      await toggleReaction(carrierId, messageId, user.id, emoji);
      await loadReactions();
    } catch (e) {
      toast.error(e?.message || 'Could not add reaction.');
    }
  };

  const reactionsByMessage = useMemo(() => {
    const map = new Map();
    for (const r of reactions) {
      if (!map.has(r.message_id)) map.set(r.message_id, {});
      const byEmoji = map.get(r.message_id);
      (byEmoji[r.emoji] ||= []).push(r.user_id);
    }
    return map;
  }, [reactions]);

  const threadItems = useMemo(() => {
    const items = [];
    let lastDateKey = null;
    let lastSenderId = null;
    let lastTime = null;
    for (const msg of messages) {
      const d = new Date(msg.created_at);
      const dateKey = d.toDateString();
      if (dateKey !== lastDateKey) {
        items.push({ type: 'date', key: `date-${dateKey}`, label: formatDateLabel(d) });
        lastDateKey = dateKey;
        lastSenderId = null;
      }
      const showHeader = !(msg.sender_id === lastSenderId && lastTime && (d - lastTime) < GROUP_WINDOW_MS);
      items.push({ type: 'message', key: msg.id, msg, showHeader });
      lastSenderId = msg.sender_id;
      lastTime = d;
    }
    return items;
  }, [messages]);

  const lastMessage = messages[messages.length - 1];
  const seenBy = useMemo(() => {
    if (!lastMessage) return [];
    return reads.filter(r => r.user_id !== user?.id && new Date(r.last_read_at) >= new Date(lastMessage.created_at));
  }, [reads, lastMessage, user?.id]);

  const filteredChannels = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? channels.filter(c => c.name.toLowerCase().includes(q)) : channels;
  }, [channels, search]);

  const filteredDms = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? dmConversations.filter(({ userId }) => nameFor(userId).toLowerCase().includes(q)) : dmConversations;
  }, [dmConversations, search, directoryMap]);

  // Owner-Op is a single-person operation -- no teammates to message.
  // Nav link is already hidden (Layout.jsx); this guard covers a direct
  // URL hit or a stale bookmark from before a plan downgrade.
  if (carrierPlan === 'owner_op') {
    return <Navigate to="/admin" replace />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-6 h-6 border-2 border-border border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex gap-4 h-[calc(100vh-140px)] min-h-[500px]">
      {/* Sidebar */}
      <div className="w-64 flex-shrink-0 glass-card flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h2 className="font-space font-semibold text-foreground">Team Chat</h2>
          <button
            onClick={() => setShowChatSettings(true)}
            title="Chat settings"
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
          >
            <SettingsIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="px-3 pt-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search"
              className="w-full pl-8 pr-2.5 py-1.5 rounded-lg bg-white/5 border border-input text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          <div className="px-3 py-1 flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Channels</span>
            <button onClick={() => setShowCreateChannel(true)} title="Create channel" className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          {filteredChannels.map(c => (
            <button
              key={c.id}
              onClick={() => setSelected({ type: 'channel', id: c.id, label: `#${c.name}` })}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-1.5 text-sm transition-colors",
                selected?.type === 'channel' && selected.id === c.id
                  ? "bg-primary/15 text-primary font-medium"
                  : "text-foreground hover:bg-white/5"
              )}
            >
              <Hash className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{c.name}</span>
            </button>
          ))}
          {search && filteredChannels.length === 0 && channels.length > 0 && (
            <p className="text-xs text-muted-foreground px-3 py-1">No channels match.</p>
          )}

          <div className="px-3 py-1 mt-3 flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Direct Messages</span>
            <button onClick={() => setShowNewMessage(true)} title="New message" className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          {filteredDms.map(({ userId }) => (
            <button
              key={userId}
              onClick={() => setSelected({ type: 'dm', id: userId, label: nameFor(userId) })}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-1.5 text-sm transition-colors",
                selected?.type === 'dm' && selected.id === userId
                  ? "bg-primary/15 text-primary font-medium"
                  : "text-foreground hover:bg-white/5"
              )}
            >
              <Avatar userId={userId} name={nameFor(userId)} avatarUrl={avatarUrlFor(userId)} size="w-6 h-6" online={onlineIds.has(userId)} />
              <span className="truncate">
                {nameFor(userId)}
                {roleFor(userId) && <span className="text-muted-foreground"> - {roleFor(userId)}</span>}
              </span>
            </button>
          ))}
          {search && filteredDms.length === 0 && dmConversations.length > 0 && (
            <p className="text-xs text-muted-foreground px-3 py-1">No conversations match.</p>
          )}
          {channels.length === 0 && dmConversations.length === 0 && (
            <p className="text-xs text-muted-foreground px-3 py-4">No channels or conversations yet.</p>
          )}
        </div>
      </div>

      {/* Thread */}
      <div className="flex-1 glass-card flex flex-col overflow-hidden">
        {!selected ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <MessageSquare className="w-8 h-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">Pick a channel or start a direct message.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
              {selected.type === 'channel' ? (
                <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Hash className="w-4 h-4 text-primary" />
                </div>
              ) : (
                <Avatar userId={selected.id} name={selected.label} avatarUrl={avatarUrlFor(selected.id)} size="w-8 h-8" online={onlineIds.has(selected.id)} />
              )}
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{selected.label}</p>
                {selected.type === 'dm' && (
                  <p className="text-xs text-muted-foreground">{onlineIds.has(selected.id) ? 'Online' : 'Offline'}</p>
                )}
              </div>
            </div>
            <div
              className="flex-1 overflow-y-auto px-4 py-4 space-y-0.5 bg-cover bg-center"
              style={chatBackground ? {
                backgroundImage: `linear-gradient(${isDark ? 'rgba(10,10,15,0.25)' : 'rgba(255,255,255,0.2)'}, ${isDark ? 'rgba(10,10,15,0.25)' : 'rgba(255,255,255,0.2)'}), ${chatBackground}`,
              } : undefined}
            >
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <p className="text-sm text-muted-foreground">No messages yet. Say hello.</p>
                </div>
              )}
              {threadItems.map(item => {
                if (item.type === 'date') return <DateSeparator key={item.key} label={item.label} />;

                const { msg, showHeader } = item;
                const mine = msg.sender_id === user.id;
                const byEmoji = reactionsByMessage.get(msg.id);
                const isLast = lastMessage && msg.id === lastMessage.id;

                return (
                  <div key={item.key} className={cn(showHeader ? 'mt-3' : 'mt-0.5')}>
                    <div className={cn("flex gap-2", mine ? "justify-end" : "justify-start")}>
                      {!mine && selected.type === 'channel' && (
                        <div className="w-6 flex-shrink-0 self-end">
                          {showHeader && <Avatar userId={msg.sender_id} name={nameFor(msg.sender_id)} avatarUrl={avatarUrlFor(msg.sender_id)} size="w-6 h-6" />}
                        </div>
                      )}
                      <div className={cn("max-w-[70%] flex flex-col gap-0.5", mine ? "items-end" : "items-start")}>
                        {showHeader && !mine && selected.type === 'channel' && (
                          <span className="text-xs font-medium text-muted-foreground px-1">{nameFor(msg.sender_id)}</span>
                        )}
                        <div className={cn('group relative flex items-center gap-1', mine ? 'flex-row-reverse' : 'flex-row')}>
                          <div className={cn(
                            "px-3.5 py-2 rounded-2xl text-sm leading-relaxed",
                            mine ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-background/80 backdrop-blur-sm border border-border text-foreground rounded-bl-sm"
                          )}>
                            {msg.body}
                          </div>
                          <button
                            onClick={() => setPickerFor(p => p === msg.id ? null : msg.id)}
                            title="Add reaction"
                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full hover:bg-white/10 text-muted-foreground flex-shrink-0"
                          >
                            <SmilePlus className="w-3.5 h-3.5" />
                          </button>
                          {pickerFor === msg.id && (
                            <div className={cn(
                              "absolute z-10 bottom-full mb-1 flex gap-0.5 bg-background border border-border rounded-full px-1.5 py-1 shadow-lg",
                              mine ? "right-0" : "left-0"
                            )}>
                              {REACTION_EMOJIS.map(e => (
                                <button key={e} onClick={() => handleToggleReaction(msg.id, e)} className="text-base leading-none hover:scale-125 transition-transform p-0.5">
                                  {e}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {byEmoji && Object.keys(byEmoji).length > 0 && (
                          <div className={cn('flex flex-wrap gap-1', mine ? 'justify-end' : 'justify-start')}>
                            {Object.entries(byEmoji).map(([emoji, userIds]) => (
                              <button
                                key={emoji}
                                onClick={() => handleToggleReaction(msg.id, emoji)}
                                title={userIds.map(nameFor).join(', ')}
                                className={cn(
                                  'px-1.5 py-0.5 rounded-full text-xs border flex items-center gap-1 transition-colors',
                                  userIds.includes(user.id) ? 'bg-primary/90 border-primary/40 text-primary-foreground' : 'bg-background/90 backdrop-blur-sm border-border text-muted-foreground hover:bg-background'
                                )}
                              >
                                <span>{emoji}</span><span>{userIds.length}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        {showHeader && (
                          <span className="text-[11px] text-muted-foreground px-1">
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                        {isLast && mine && seenBy.length > 0 && (
                          <span className="flex items-center gap-1 text-[11px] text-muted-foreground px-1">
                            <CheckCheck className="w-3 h-3 text-primary" />
                            {selected.type === 'dm' ? 'Seen' : `Seen by ${seenBy.map(r => nameFor(r.user_id)).join(', ')}`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            <div className="px-4 py-3 border-t border-border flex gap-2">
              <textarea
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message… (Enter to send)"
                rows={1}
                className="flex-1 px-3 py-2 rounded-xl bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary/50"
              />
              <button
                onClick={handleSend}
                disabled={sending || !input.trim()}
                className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center flex-shrink-0 hover:bg-primary/90 disabled:opacity-40 transition-colors self-end"
              >
                <Send className="w-4 h-4 text-primary-foreground" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* New Message modal */}
      {showNewMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowNewMessage(false)} />
          <div className="relative bg-background border border-border rounded-2xl w-full max-w-sm z-10 max-h-[70vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
              <h3 className="font-space font-semibold text-foreground">New Message</h3>
              <button onClick={() => setShowNewMessage(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-y-auto py-2">
              {directory.filter(s => s.user_id !== user.id).map(s => (
                <button
                  key={s.id}
                  onClick={() => handleStartDM(s)}
                  className="w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-white/5 transition-colors"
                >
                  <Avatar userId={s.user_id} name={s.full_name || s.user_email} avatarUrl={s.avatar_url} size="w-8 h-8" online={onlineIds.has(s.user_id)} />
                  <div className="min-w-0">
                    <p className="text-sm text-foreground truncate">{s.full_name || s.user_email}</p>
                    <p className="text-xs text-muted-foreground">{ROLE_LABELS[s.staff_role] || s.staff_role}</p>
                  </div>
                </button>
              ))}
              {directory.length <= 1 && (
                <p className="text-xs text-muted-foreground px-5 py-4">No other staff members yet.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Channel modal */}
      {showCreateChannel && (
        <CreateChannelModal
          onClose={() => setShowCreateChannel(false)}
          onCreate={async (name, description) => {
            try {
              const c = await createChannel(carrierId, user.id, name, description);
              setShowCreateChannel(false);
              await loadSidebar();
              setSelected({ type: 'channel', id: c.id, label: `#${c.name}` });
            } catch (e) {
              toast.error(e?.message || 'Could not create channel.');
            }
          }}
        />
      )}

      {/* Chat Settings modal -- personal background, this user only */}
      {showChatSettings && (
        <ChatSettingsModal
          current={chatBackground}
          onApply={applyChatBackground}
          onClose={() => setShowChatSettings(false)}
        />
      )}
    </div>
  );
}

function CreateChannelModal({ onClose, onCreate }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Channel name is required.'); return; }
    setSaving(true);
    await onCreate(name, description);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-sm z-10 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-space font-semibold text-foreground">Create Channel</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Name</label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="maintenance"
              autoFocus
              className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Description (optional)</label>
            <input
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What's this channel for?"
              className="w-full px-3 py-2 rounded-lg bg-transparent border border-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary"
            />
          </div>
          <button type="submit" disabled={saving} className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50">
            {saving ? 'Creating…' : 'Create Channel'}
          </button>
        </form>
      </div>
    </div>
  );
}

// Hard cap on chat background uploads -- 4K (3840x2160) is the largest size
// that's ever worth it here (the panel is bg-cover, so nothing beyond the
// viewer's own screen resolution adds visible sharpness). The byte cap was
// originally 8MB but a real 3840x2160 JPEG of a high-detail/high-contrast
// photo (lots of fine texture, e.g. a neon sign against a dark background)
// routinely lands well above that -- 8MB assumed a fairly compressible
// photo, not a worst-case one. 20MB comfortably covers legitimate 4K
// JPEG/WebP photos at any reasonable quality setting while still blocking
// raw camera dumps or uncompressed PNGs that would reintroduce the
// slow-load bug this was built to fix -- see uploadChatBackground in
// lib/teamChat.js.
const MAX_BG_BYTES = 20 * 1024 * 1024;
const MAX_BG_WIDTH = 3840;
const MAX_BG_HEIGHT = 2160;

// Browsers don't expose image dimensions without decoding it first.
function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that image file.')); };
    img.src = url;
  });
}

function ChatSettingsModal({ current, onApply, onClose }) {
  const { user, carrierId } = useAuth();
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > MAX_BG_BYTES) {
      toast.error(`Image is too large (${(file.size / (1024 * 1024)).toFixed(1)}MB) — max ${MAX_BG_BYTES / (1024 * 1024)}MB.`);
      return;
    }
    let width, height;
    try {
      ({ width, height } = await getImageDimensions(file));
    } catch (err) {
      toast.error(err.message);
      return;
    }
    if (width > MAX_BG_WIDTH || height > MAX_BG_HEIGHT) {
      toast.error(`Image is ${width}×${height} — max ${MAX_BG_WIDTH}×${MAX_BG_HEIGHT} (4K).`);
      return;
    }

    setUploading(true);
    try {
      const url = await uploadChatBackground(carrierId, user.id, file);
      onApply(`url(${url})`);
      toast.success('Background updated.');
    } catch (err) {
      toast.error(err?.message || 'Could not upload image.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-sm z-10 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-space font-semibold text-foreground">Chat Background</h3>
            <p className="text-xs text-muted-foreground mt-0.5">Only visible to you.</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        <div>
          <p className="text-xs text-muted-foreground mb-2">Presets</p>
          <div className="grid grid-cols-5 gap-2">
            {BG_PRESETS.map(p => (
              <button
                key={p.label}
                title={p.label}
                onClick={() => onApply(p.value)}
                className={cn(
                  "aspect-square rounded-lg border-2 transition-all",
                  current === p.value ? "border-primary" : "border-transparent hover:border-border"
                )}
                style={{ backgroundImage: p.value }}
              />
            ))}
          </div>
        </div>

        <div>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-dashed border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading…' : 'Upload your own image'}
          </button>
        </div>

        {current && (
          <button
            onClick={() => onApply(null)}
            className="w-full py-2 rounded-lg bg-white/5 border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-white/10 transition-colors"
          >
            Remove Background
          </button>
        )}
      </div>
    </div>
  );
}
